import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { AmbientBg } from "@/components/studio/atoms";
import { ChatPromptDock } from "@/components/studio/ChatPromptDock";
import { SubjectLockPill } from "@/components/studio/SubjectLockPill";
import { useSubjectLock } from "@/hooks/useSubjectLock";
import { useAuth } from "@/context/AuthContext";
import { pollJob } from "@/hooks/useGenerationQueue";
import {
  Loader2, Upload, X, Wand2, Mic, Play, Sparkle, LayoutGrid, ImageIcon,
  Volume2, Send, Download, UserPlus, Check, Bookmark, BookmarkPlus,
} from "lucide-react";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";

export default function AvatarStudio() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Portrait (image) — upload OR pick from gallery
  const [portrait, setPortrait] = useState(null); // {url, source: "upload"|"gallery"}
  const [uploadingPortrait, setUploadingPortrait] = useState(false);
  const portraitInputRef = useRef(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(false);

  // Audio source — upload OR text-to-speech
  const [audio, setAudio] = useState(null); // {url, source: "upload"|"tts", label}
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const audioInputRef = useRef(null);
  const [script, setScript] = useState("");
  const [voices, setVoices] = useState([]);
  const [voice, setVoice] = useState("rachel");
  const [generatingTts, setGeneratingTts] = useState(false);
  // Voice-delivery controls — speech style + emotion intensity map to
  // ElevenLabs `style` + stability/similarity_boost knobs on the backend.
  const [speechStyle, setSpeechStyle] = useState("neutral");  // neutral | storyteller | dramatic | whisper | energetic
  const [emotionIdx, setEmotionIdx] = useState(1);            // 0 Calm · 1 Expressive · 2 Intense
  // Voice-delivery presets — named snapshots of (voice + style + emotion)
  // the user can save and re-apply in one tap. Persisted per-user in Mongo.
  const [presets, setPresets] = useState([]);
  const [presetSaving, setPresetSaving] = useState(false);
  const EMOTION_STOPS = ["calm", "expressive", "intense"];
  const EMOTION_LABELS = ["CALM", "EXPRESSIVE", "INTENSE"];
  const SPEECH_STYLES = [
    { id: "neutral",     label: "Neutral",     hint: "Clean, documentary" },
    { id: "storyteller", label: "Storyteller", hint: "Warm, cinematic cadence" },
    { id: "dramatic",    label: "Dramatic",    hint: "Big movie-trailer energy" },
    { id: "whisper",     label: "Whisper",     hint: "Breathy, intimate ASMR" },
    { id: "energetic",   label: "Energetic",   hint: "Hype, high-tempo delivery" },
  ];
  // Group voices by `group` for the dropdown — Emergent first, then African,
  // then existing Preset, then user's cloned voice. Falls back to flat list
  // if the backend didn't tag them (e.g. older cached response).
  const groupedVoices = (() => {
    const g = { emergent: [], african: [], preset: [], mine: [] };
    (voices || []).forEach((v) => {
      const bucket = g[v.group] || g.preset;
      bucket.push(v);
    });
    return g;
  })();

  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState("");
  const [result, setResult] = useState(null);

  // Voice-clone inline flow
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneName, setCloneName] = useState("My Voice");
  const [cloneBusy, setCloneBusy] = useState(false);
  const cloneFileRef = useRef(null);

  const refreshVoices = () => api.get("/tts/voices").then((r) => setVoices(r.data?.voices || []));
  const refreshPresets = () => api.get("/voice-presets").then((r) => setPresets(r.data?.presets || []));

  const applyPreset = (preset) => {
    if (!preset) return;
    // Only apply the voice if it still exists in the current roster — a
    // user's cloned voice could have been deleted, or a shared library
    // voice could have been pulled by ElevenLabs. Fall back silently.
    const stillAvailable = voices.some((v) => v.id === preset.voice);
    if (stillAvailable) setVoice(preset.voice);
    setSpeechStyle(preset.style_label || "neutral");
    const idx = EMOTION_STOPS.indexOf(preset.emotion || "expressive");
    setEmotionIdx(idx === -1 ? 1 : idx);
    toast.success(`"${preset.name}" applied${stillAvailable ? "" : " (voice unavailable — kept current)"}`);
  };

  const saveCurrentAsPreset = async () => {
    const defaultName = voices.find((v) => v.id === voice)?.label?.split(" · ")[0] || "My Preset";
    const name = window.prompt("Name this voice preset (e.g. 'Trailer Boom', 'Soft Narrator')", defaultName);
    if (!name) return;
    setPresetSaving(true);
    try {
      await api.post("/voice-presets", {
        name,
        voice,
        style_label: speechStyle,
        emotion: EMOTION_STOPS[Math.max(0, Math.min(2, emotionIdx))],
      });
      await refreshPresets();
      toast.success(`"${name.trim()}" saved — tap it anytime to recall.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save preset.");
    } finally {
      setPresetSaving(false);
    }
  };

  const deletePreset = async (preset) => {
    // Optimistic — drop from UI first; refresh if the API disagrees.
    setPresets((prev) => prev.filter((p) => p.preset_id !== preset.preset_id));
    try {
      await api.delete(`/voice-presets/${preset.preset_id}`);
      toast.success(`"${preset.name}" removed.`);
    } catch {
      refreshPresets();
      toast.error("Couldn't remove preset.");
    }
  };

  const hasCloned = voices.some((v) => v.id === "mine");
  const myVoice = voices.find((v) => v.id === "mine");

  useEffect(() => {
    refreshVoices().catch(() => {});
    refreshPresets().catch(() => {});
  }, []);

  // Route-in: /avatar with { state: { portrait: {url}} }
  useEffect(() => {
    const incoming = location.state?.portrait;
    if (incoming?.url) {
      setPortrait({ url: incoming.url, source: "gallery" });
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const openGallery = async () => {
    setGalleryOpen(true);
    if (gallery.length === 0) {
      setLoadingGallery(true);
      try {
        const { data } = await api.get("/generations?scope=mine");
        setGallery((data || []).filter((g) => g.image_url && !g.video_url));
      } catch { toast.error("Failed to load gallery"); }
      finally { setLoadingGallery(false); }
    }
  };

  const uploadPortrait = async (file) => {
    if (!file?.type?.startsWith("image/")) { toast.error("Only image files allowed."); return; }
    setUploadingPortrait(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("note", "Avatar portrait");
      const { data } = await api.post("/upload/image", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPortrait({ url: data.image_url, source: "upload" });
      toast.success("Portrait ready.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploadingPortrait(false); }
  };

  const uploadAudio = async (file) => {
    if (!file?.type?.startsWith("audio/")) { toast.error("Only audio files allowed (mp3, wav, m4a)."); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("Audio must be under 25MB."); return; }
    setUploadingAudio(true);
    try {
      // Persist the file to our storage via /upload/audio so the backend
      // can hand fal.ai a real HTTPS URL (data: URIs are rejected by sync-lipsync).
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload/audio", form, { headers: { "Content-Type": "multipart/form-data" } });
      if (!data?.audio_url) throw new Error("No audio URL returned");
      setAudio({ url: data.audio_url, source: "upload", label: file.name });
      toast.success("Audio staged.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audio upload failed");
    } finally { setUploadingAudio(false); }
  };

  const generateTts = async () => {
    const text = script.trim();
    if (text.length < 4) { toast.error("Write a few more words."); return; }
    setGeneratingTts(true);
    try {
      const emotion = EMOTION_STOPS[Math.max(0, Math.min(2, emotionIdx))];
      const { data } = await api.post("/tts/preview", {
        text,
        voice,
        style_label: speechStyle,
        emotion,
      });
      const url = data.audio_url || data.audio_data_url;
      if (!url) throw new Error("no audio URL");
      const activeVoiceLabel = voices.find((v) => v.id === voice)?.label || voice;
      setAudio({
        url,
        source: "tts",
        label: `${activeVoiceLabel} · ${speechStyle} · ${emotion}`,
      });
      toast.success("Voice-over ready.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voice-over failed");
    } finally { setGeneratingTts(false); }
  };

  const generate = async () => {
    if (!portrait?.url) { toast.error("Add a portrait first."); return; }
    if (!audio?.url) { toast.error("Add or record audio first."); return; }
    setGenerating(true);
    setGenProgress("Queueing avatar job…");
    setResult(null);
    try {
      // Submit + get a job id back immediately (no long-lived HTTP call so
      // Cloudflare's 100s edge timeout can't drop the connection).
      const { data } = await api.post("/avatar/lipsync", {
        image_url: portrait.url,
        audio_url: audio.url,
        prompt: prompt || undefined,
      });
      if (!data?.job_id) throw new Error("Server did not return a job id.");
      // Poll every 5s until the job completes or fails.
      const finalResult = await pollJob(data.job_id, (job) => {
        const p = job?.progress || {};
        if (p.label) setGenProgress(p.label + (p.total ? ` (${p.step}/${p.total})` : ""));
      });
      setResult(finalResult);
      setGenProgress("");
      toast.success("Talking avatar ready.");
    } catch (e) {
      const err = e?.jobError || e?.response?.data?.detail || e?.message;
      const msg = (typeof err === "object" && err?.message) ? err.message : (err || "Generation failed");
      const refunded = (typeof err === "object" && err?.refunded) ? err.refunded : null;
      toast.error(refunded ? `${msg} · ${refunded} credit${refunded === 1 ? "" : "s"} refunded` : msg);
      setGenProgress("");
    } finally { setGenerating(false); }
  };

  const submitVoiceClone = async (file) => {
    if (!file?.type?.startsWith("audio/")) { toast.error("Upload an audio sample (mp3/wav/m4a)."); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error("Sample must be under 20MB."); return; }
    setCloneBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", cloneName || "My Voice");
      await api.post("/voice/clone", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Voice cloned. It's now available as 'My Voice'.");
      setCloneOpen(false);
      await refreshVoices();
      setVoice("mine");  // auto-select the newly cloned voice
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voice clone failed");
    } finally { setCloneBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#07070C] glyph-bg relative">
      <AmbientBg variant="gallery" />
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="relative mb-6 rounded-3xl overflow-hidden border border-[#F2C94C]/25 bg-gradient-to-br from-[#1a0d24] via-[#0A0A0A] to-[#0d1f24] p-6 md:p-7 action-stripes">
          <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-[#F2C94C]/15 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 font-mono-hy text-[10px] text-[#F2C94C] mb-3 px-3 py-1 rounded-full bg-[#F2C94C]/10 border border-[#F2C94C]/30">
              <Sparkle className="w-3 h-3" /> AI AVATAR · TALKING PORTRAIT
            </div>
            <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
              Bring a portrait to <span className="bg-gradient-to-r from-[#F2C94C] via-[#E27D60] to-[#00E5FF] bg-clip-text text-transparent">life</span>.
            </h1>
            <p className="font-body text-[#D9D2BE] mt-2 max-w-2xl text-sm md:text-base">
              Upload a character portrait or pick one from your Vault, then attach an audio clip or type a script. Our lipsync engine syncs mouth movement to the voice.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Portrait */}
          <div className="p-5 rounded-2xl bg-[#120E15] border border-[#00E5FF]/25 space-y-3" data-testid="avatar-portrait-card">
            <div className="font-mono-hy text-[10px] text-[#00E5FF] flex items-center gap-1.5">
              <ImageIcon className="w-3 h-3" /> // Portrait
            </div>
            {portrait?.url ? (
              <div className="relative aspect-square rounded-xl overflow-hidden border-2 border-[#00E5FF]/40">
                <img src={portrait.url} alt="portrait" className="w-full h-full object-cover" data-testid="avatar-portrait-img" />
                <button
                  onClick={() => setPortrait(null)}
                  data-testid="avatar-portrait-clear"
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-[#0A0A0A]/85 border border-[#E27D60]/40 text-[#E27D60] hover:bg-[#E27D60] hover:text-white flex items-center justify-center"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                <div className="absolute bottom-2 left-2 font-mono-hy text-[9px] px-2 py-0.5 rounded-full bg-[#0A0A0A]/85 border border-[#00E5FF]/40 text-[#00E5FF] tracking-widest">
                  {portrait.source === "gallery" ? "GALLERY" : "UPLOAD"}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 aspect-square rounded-xl border-2 border-dashed border-[#00E5FF]/25 p-2">
                <button
                  onClick={() => portraitInputRef.current?.click()}
                  disabled={uploadingPortrait}
                  data-testid="avatar-portrait-upload"
                  className="rounded-xl border border-[#00E5FF]/20 hover:bg-[#00E5FF]/5 flex flex-col items-center justify-center gap-2 text-[#00E5FF]/70 hover:text-[#00E5FF] transition-all disabled:opacity-50"
                >
                  {uploadingPortrait ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
                  <span className="font-mono-hy text-[9px] tracking-widest">UPLOAD</span>
                </button>
                <button
                  onClick={openGallery}
                  data-testid="avatar-portrait-gallery"
                  className="rounded-xl border border-[#F2C94C]/20 hover:bg-[#F2C94C]/5 flex flex-col items-center justify-center gap-2 text-[#F2C94C]/70 hover:text-[#F2C94C] transition-all"
                >
                  <LayoutGrid className="w-6 h-6" />
                  <span className="font-mono-hy text-[9px] tracking-widest">GALLERY</span>
                </button>
              </div>
            )}
            <input
              ref={portraitInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="avatar-portrait-input"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPortrait(f); e.target.value = ""; }}
            />
          </div>

          {/* Audio */}
          <div className="p-5 rounded-2xl bg-[#120E15] border border-[#F2C94C]/25 space-y-3" data-testid="avatar-audio-card">
            <div className="font-mono-hy text-[10px] text-[#F2C94C] flex items-center justify-between gap-1.5">
              <span className="flex items-center gap-1.5">
                <Mic className="w-3 h-3" /> // Voice
              </span>
              {hasCloned ? (
                <button
                  onClick={() => setVoice("mine")}
                  data-testid="avatar-use-cloned-voice"
                  className={`font-mono-hy text-[9px] px-2 py-1 rounded-full border flex items-center gap-1 transition-all ${
                    voice === "mine"
                      ? "bg-[#F2C94C] text-[#0A0A0A] border-[#F2C94C]"
                      : "border-[#F2C94C]/40 text-[#F2C94C] hover:bg-[#F2C94C]/10"
                  }`}
                  title={myVoice?.label}
                >
                  {voice === "mine" ? <Check className="w-3 h-3" /> : <Sparkle className="w-3 h-3" />}
                  {voice === "mine" ? "USING MY VOICE" : "USE MY VOICE"}
                </button>
              ) : (
                <button
                  onClick={() => setCloneOpen(true)}
                  data-testid="avatar-clone-voice-btn"
                  className="font-mono-hy text-[9px] px-2 py-1 rounded-full border border-[#E27D60]/40 text-[#E27D60] hover:bg-[#E27D60]/10 flex items-center gap-1"
                >
                  <UserPlus className="w-3 h-3" /> CLONE MY VOICE
                </button>
              )}
            </div>
            {audio?.url ? (
              <div className="p-3 rounded-xl border-2 border-[#F2C94C]/40 bg-[#0A0A0A] space-y-2">
                <div className="flex items-center gap-2">
                  <Volume2 className="w-4 h-4 text-[#F2C94C]" />
                  <div className="flex-1 min-w-0">
                    <div className="font-body text-sm text-white truncate">{audio.label || "Audio track"}</div>
                    <div className="font-mono-hy text-[9px] text-white">{audio.source === "tts" ? "GENERATED VOICE" : "UPLOADED AUDIO"}</div>
                  </div>
                  <button
                    onClick={() => setAudio(null)}
                    data-testid="avatar-audio-clear"
                    className="w-7 h-7 rounded-full text-[#E27D60] hover:bg-[#E27D60]/10 flex items-center justify-center"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <audio
                  controls
                  src={audio.url}
                  className="w-full"
                  data-testid="avatar-audio-preview"
                  onError={() => toast.error("Couldn't load the generated audio — try again or pick a different voice.")}
                />
              </div>
            ) : (
              <>
                {cloneOpen && (
                  <div className="p-3 rounded-xl border border-[#E27D60]/40 bg-[#E27D60]/5 space-y-2" data-testid="avatar-clone-panel">
                    <div className="flex items-center justify-between">
                      <div className="font-mono-hy text-[10px] text-[#E27D60] flex items-center gap-1.5">
                        <UserPlus className="w-3 h-3" /> CLONE YOUR VOICE
                      </div>
                      <button
                        onClick={() => setCloneOpen(false)}
                        data-testid="avatar-clone-close"
                        className="w-6 h-6 rounded-full text-white hover:text-[#E27D60]"
                      ><X className="w-3.5 h-3.5" /></button>
                    </div>
                    <p className="font-mono-hy text-[9px] text-white">Upload a clean 20-60s sample of your voice. It'll appear as "My Voice" here and in Studio narration.</p>
                    <input
                      value={cloneName}
                      onChange={(e) => setCloneName(e.target.value.slice(0, 40))}
                      placeholder="Give it a name (e.g. Emmy · Cinematic)"
                      data-testid="avatar-clone-name"
                      className="w-full px-3 py-2 rounded-lg bg-[#0A0A0A] border border-[#E27D60]/25 focus:border-[#E27D60] focus:outline-none font-body text-sm text-white placeholder:text-white"
                    />
                    <input
                      ref={cloneFileRef}
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      data-testid="avatar-clone-input"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) submitVoiceClone(f); e.target.value = ""; }}
                    />
                    <button
                      onClick={() => cloneFileRef.current?.click()}
                      disabled={cloneBusy}
                      data-testid="avatar-clone-submit"
                      className="w-full font-mono-hy text-[10px] px-4 py-2 rounded-full bg-[#E27D60] text-[#0A0A0A] hover:brightness-110 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {cloneBusy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Cloning…</> : <><Upload className="w-3.5 h-3.5" /> Upload sample & clone</>}
                    </button>
                  </div>
                )}
                <div className="p-3 rounded-xl border border-[#F2C94C]/15 space-y-3">
                  <textarea
                    value={script}
                    onChange={(e) => setScript(e.target.value.slice(0, 600))}
                    placeholder="Type a script — Nova will speak it in the chosen voice."
                    rows={3}
                    data-testid="avatar-tts-script"
                    className="w-full min-h-[80px] px-3 py-2 rounded-lg bg-[#0A0A0A] border border-[#F2C94C]/20 focus:border-[#00E5FF] focus:outline-none font-body text-sm text-white placeholder:text-white resize-none"
                  />

                  {/* Voice picker — grouped: AFRICAN VOICES, PRESET VOICES, MY VOICE */}
                  {voices.length > 0 && (
                    <div className="space-y-1.5">
                      <div className="font-mono-hy text-[9px] text-[#F2C94C] tracking-[0.2em]">
                        VOICE
                      </div>
                      <Select value={voice} onValueChange={setVoice}>
                        <SelectTrigger
                          data-testid="avatar-tts-voice"
                          className="w-full bg-[#0A0A0A] border-[#F2C94C]/25 h-9 font-body text-xs text-white hover:border-[#F2C94C]/50 transition-colors"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#120E15] border-[#F2C94C]/30 max-h-[360px]">
                          {groupedVoices.emergent.length > 0 && (
                            <SelectGroup>
                              <SelectLabel
                                data-testid="avatar-tts-group-emergent"
                                className="font-mono-hy text-[9px] text-[#B18AF2] tracking-[0.22em] px-2 py-1.5 flex items-center gap-1.5"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-[#B18AF2]" /> EMERGENT VOICES
                              </SelectLabel>
                              {groupedVoices.emergent.map((v) => (
                                <SelectItem
                                  key={v.id}
                                  value={v.id}
                                  data-testid={`avatar-tts-voice-opt-${v.id}`}
                                  className="font-body text-xs pl-5 text-white focus:bg-[#B18AF2]/10 focus:text-white"
                                >
                                  {v.label}
                                </SelectItem>
                              ))}
                              <SelectSeparator className="bg-[#B18AF2]/15 my-1" />
                            </SelectGroup>
                          )}
                          {groupedVoices.african.length > 0 && (
                            <SelectGroup>
                              <SelectLabel
                                data-testid="avatar-tts-group-african"
                                className="font-mono-hy text-[9px] text-[#00E5FF] tracking-[0.22em] px-2 py-1.5 flex items-center gap-1.5"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF]" /> AFRICAN VOICES
                              </SelectLabel>
                              {groupedVoices.african.map((v) => (
                                <SelectItem
                                  key={v.id}
                                  value={v.id}
                                  data-testid={`avatar-tts-voice-opt-${v.id}`}
                                  className="font-body text-xs pl-5 text-white focus:bg-[#00E5FF]/10 focus:text-white"
                                >
                                  {v.label}
                                </SelectItem>
                              ))}
                              <SelectSeparator className="bg-[#F2C94C]/15 my-1" />
                            </SelectGroup>
                          )}
                          {groupedVoices.preset.length > 0 && (
                            <SelectGroup>
                              <SelectLabel
                                data-testid="avatar-tts-group-preset"
                                className="font-mono-hy text-[9px] text-[#F2C94C] tracking-[0.22em] px-2 py-1.5 flex items-center gap-1.5"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-[#F2C94C]" /> PRESET VOICES
                              </SelectLabel>
                              {groupedVoices.preset.map((v) => (
                                <SelectItem
                                  key={v.id}
                                  value={v.id}
                                  data-testid={`avatar-tts-voice-opt-${v.id}`}
                                  className="font-body text-xs pl-5 text-white focus:bg-[#F2C94C]/10 focus:text-white"
                                >
                                  {v.label}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                          {groupedVoices.mine.length > 0 && (
                            <>
                              <SelectSeparator className="bg-[#E27D60]/15 my-1" />
                              <SelectGroup>
                                <SelectLabel
                                  data-testid="avatar-tts-group-mine"
                                  className="font-mono-hy text-[9px] text-[#E27D60] tracking-[0.22em] px-2 py-1.5 flex items-center gap-1.5"
                                >
                                  <span className="w-1.5 h-1.5 rounded-full bg-[#E27D60]" /> MY VOICE
                                </SelectLabel>
                                {groupedVoices.mine.map((v) => (
                                  <SelectItem
                                    key={v.id}
                                    value={v.id}
                                    data-testid={`avatar-tts-voice-opt-${v.id}`}
                                    className="font-body text-xs pl-5 text-white focus:bg-[#E27D60]/10 focus:text-white"
                                  >
                                    ⭐ {v.label}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Speech Style — dropdown that drives ElevenLabs `style` */}
                  <div className="space-y-1.5">
                    <div className="font-mono-hy text-[9px] text-[#F2C94C] tracking-[0.2em]">
                      SPEECH STYLE
                    </div>
                    <Select value={speechStyle} onValueChange={setSpeechStyle}>
                      <SelectTrigger
                        data-testid="avatar-tts-style"
                        className="w-full bg-[#0A0A0A] border-[#F2C94C]/25 h-9 font-body text-xs text-white hover:border-[#F2C94C]/50 transition-colors"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#120E15] border-[#F2C94C]/30">
                        {SPEECH_STYLES.map((s) => (
                          <SelectItem
                            key={s.id}
                            value={s.id}
                            data-testid={`avatar-tts-style-opt-${s.id}`}
                            className="font-body text-xs text-white focus:bg-[#F2C94C]/10 focus:text-white"
                          >
                            <div className="flex flex-col items-start">
                              <span>{s.label}</span>
                              <span className="font-mono-hy text-[9px] text-white/60">{s.hint}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Emotion Intensity — slider mapped to stability + similarity_boost */}
                  <div className="space-y-1.5" data-testid="avatar-tts-emotion-row">
                    <div className="flex items-center justify-between">
                      <div className="font-mono-hy text-[9px] text-[#F2C94C] tracking-[0.2em]">
                        EMOTION INTENSITY
                      </div>
                      <div
                        data-testid="avatar-tts-emotion-value"
                        className="font-mono-hy text-[9px] text-[#00E5FF] tracking-[0.22em]"
                      >
                        {EMOTION_LABELS[emotionIdx]}
                      </div>
                    </div>
                    <Slider
                      value={[emotionIdx]}
                      min={0}
                      max={2}
                      step={1}
                      onValueChange={(v) => setEmotionIdx(v[0])}
                      data-testid="avatar-tts-emotion-slider"
                      className="py-2"
                    />
                    <div className="flex justify-between font-mono-hy text-[8.5px] text-white/55 tracking-[0.2em] px-0.5">
                      {EMOTION_LABELS.map((l, i) => (
                        <button
                          key={l}
                          type="button"
                          onClick={() => setEmotionIdx(i)}
                          data-testid={`avatar-tts-emotion-stop-${EMOTION_STOPS[i]}`}
                          className={`transition-colors ${i === emotionIdx ? "text-[#00E5FF]" : "hover:text-white/80"}`}
                        >
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Saved voice-delivery presets — tap a pill to re-apply
                      the full (voice + style + emotion) snapshot. Save pill
                      captures the current state. */}
                  <div className="space-y-1.5" data-testid="avatar-tts-presets-row">
                    <div className="flex items-center justify-between">
                      <div className="font-mono-hy text-[9px] text-[#F2C94C] tracking-[0.2em] flex items-center gap-1.5">
                        <Bookmark className="w-2.5 h-2.5" /> MY PRESETS
                      </div>
                      <button
                        type="button"
                        onClick={saveCurrentAsPreset}
                        disabled={presetSaving || !voice}
                        data-testid="avatar-tts-preset-save"
                        title="Save current voice + style + emotion as a named preset"
                        className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 hover:border-[#00E5FF] hover:shadow-[0_0_16px_-4px_rgba(0,229,255,0.6)] disabled:opacity-50 transition-all font-mono-hy text-[9px] tracking-[0.18em]"
                      >
                        {presetSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <BookmarkPlus className="w-3 h-3" />}
                        SAVE PRESET
                      </button>
                    </div>
                    {presets.length === 0 ? (
                      <div className="font-body text-[11px] text-white/50 italic px-1" data-testid="avatar-tts-presets-empty">
                        No presets saved yet. Dial in a voice + style + emotion and tap SAVE PRESET.
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {presets.map((p) => {
                          const isActive =
                            p.voice === voice &&
                            p.style_label === speechStyle &&
                            p.emotion === EMOTION_STOPS[emotionIdx];
                          return (
                            <div
                              key={p.preset_id}
                              className={`group relative inline-flex items-center gap-1.5 pl-2.5 pr-1.5 h-7 rounded-full border transition-all ${
                                isActive
                                  ? "border-[#F2C94C] bg-[#F2C94C]/15 text-[#F2C94C] shadow-[0_0_14px_-4px_rgba(242,201,76,0.6)]"
                                  : "border-white/15 text-white/80 hover:border-[#F2C94C]/60 hover:bg-[#F2C94C]/5 hover:text-white"
                              }`}
                              data-testid={`avatar-tts-preset-${p.preset_id}`}
                              title={`${p.voice} · ${p.style_label} · ${p.emotion}`}
                            >
                              <button
                                type="button"
                                onClick={() => applyPreset(p)}
                                data-testid={`avatar-tts-preset-apply-${p.preset_id}`}
                                className="font-mono-hy text-[10px] tracking-[0.14em] truncate max-w-[160px]"
                              >
                                {isActive && <Check className="w-3 h-3 inline mr-1 -mt-0.5" />}
                                {p.name}
                              </button>
                              <button
                                type="button"
                                onClick={() => deletePreset(p)}
                                data-testid={`avatar-tts-preset-delete-${p.preset_id}`}
                                title="Delete preset"
                                aria-label={`Delete preset ${p.name}`}
                                className="w-4 h-4 rounded-full flex items-center justify-center text-white/50 hover:text-[#EB5757] hover:bg-[#EB5757]/15 transition-all"
                              >
                                <X className="w-2.5 h-2.5" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={generateTts}
                    disabled={generatingTts || script.trim().length < 4}
                    data-testid="avatar-tts-generate"
                    className="w-full font-mono-hy text-[10px] px-4 py-2.5 rounded-full bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] hover:shadow-[0_0_20px_-4px_rgba(242,201,76,0.65)] disabled:opacity-50 flex items-center justify-center gap-2 transition-all"
                  >
                    {generatingTts ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Speak it
                  </button>
                </div>
                <div className="text-center font-mono-hy text-[9px] text-white">— OR —</div>
                <button
                  onClick={() => audioInputRef.current?.click()}
                  disabled={uploadingAudio}
                  data-testid="avatar-audio-upload"
                  className="w-full py-3 rounded-xl border-2 border-dashed border-[#F2C94C]/25 text-[#F2C94C] hover:bg-[#F2C94C]/5 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {uploadingAudio ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  <span className="font-mono-hy text-[10px] tracking-widest">UPLOAD AUDIO · MP3 · WAV</span>
                </button>
                <input
                  ref={audioInputRef}
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  data-testid="avatar-audio-input"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAudio(f); e.target.value = ""; }}
                />
              </>
            )}
          </div>
        </div>

        {/* Prompt + Generate */}
        <div className="mt-6 p-5 rounded-2xl bg-[#120E15] border border-[#F2C94C]/40 space-y-3">
          <div className="font-mono-hy text-[10px] text-[#F2C94C]">// Optional performance direction</div>
          <input
            value={prompt}
            onChange={(e) => setPrompt(e.target.value.slice(0, 240))}
            placeholder="e.g. warm, confident, storyteller tone"
            data-testid="avatar-prompt"
            className="w-full px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#F2C94C]/20 focus:border-[#00E5FF] focus:outline-none font-body text-sm text-white placeholder:text-white"
          />
          <button
            onClick={generate}
            disabled={generating || !portrait?.url || !audio?.url}
            data-testid="avatar-generate-btn"
            className="w-full font-mono-hy text-[11px] px-5 py-3.5 rounded-xl bg-gradient-to-r from-[#F2C94C] via-[#E27D60] to-[#00E5FF] text-[#0A0A0A] hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> {genProgress || "Syncing lips…"}</> : <><Send className="w-4 h-4" /> Generate talking avatar · 5 cr</>}
          </button>
        </div>

        {result?.video_url && (
          <div className="mt-6 p-4 rounded-2xl bg-[#120E15] border border-[#F2C94C]/30" data-testid="avatar-result-card">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="font-mono-hy text-[10px] text-[#00E5FF]">// AVATAR READY</div>
              <a
                href={result.video_url}
                download
                target="_blank"
                rel="noreferrer"
                data-testid="avatar-download-btn"
                className="font-mono-hy text-[10px] px-4 py-2 rounded-full bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] flex items-center gap-2"
              >
                <Download className="w-3.5 h-3.5" /> Download MP4
              </a>
            </div>
            <video src={result.video_url} controls autoPlay loop className="w-full max-h-[600px] rounded-xl border border-[#F2C94C]/20" data-testid="avatar-result-video" />
          </div>
        )}
      </div>

      {/* Gallery picker */}
      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent className="max-w-4xl bg-[#120E15] border-[#F2C94C]/25 text-white">
          <DialogTitle className="sr-only">Pick a portrait from your gallery</DialogTitle>
          <div className="p-2">
            <div className="font-mono-hy text-[10px] text-[#F2C94C] mb-3">// Pick a portrait from your gallery</div>
            {loadingGallery ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#F2C94C]" /></div>
            ) : gallery.length === 0 ? (
              <div className="text-center py-12 font-mono-hy text-[10px] text-white">No portraits yet. Create one in Studio first.</div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3 max-h-[60vh] overflow-y-auto">
                {gallery.map((g) => (
                  <button
                    key={g.generation_id}
                    onClick={() => { setPortrait({ url: g.image_url, source: "gallery" }); setGalleryOpen(false); }}
                    data-testid={`avatar-gallery-pick-${g.generation_id}`}
                    className="group aspect-square rounded-xl overflow-hidden border border-[#F2C94C]/15 hover:border-[#F2C94C] transition-all"
                  >
                    <img src={g.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <Footer />
      {/* Session subject lock — carried across Studio, Storyboard, and Avatar */}
      <AvatarSubjectLock />
      {/* Universal composer — routes to /studio for image/video, keeps you here for avatar */}
      <AvatarDock />
    </div>
  );
}

function AvatarSubjectLock() {
  const { lock, clearLock } = useSubjectLock();
  return <SubjectLockPill lock={lock} onClear={clearLock} />;
}

function AvatarDock() {
  const [prompt, setPrompt] = useState("");
  const [dockMode, setDockMode] = useState("image");
  const [autoOn, setAutoOn] = useState(true);
  const navigate = useNavigate();
  return (
    <ChatPromptDock
      mode={dockMode}
      setMode={setDockMode}
      prompt={prompt}
      setPrompt={setPrompt}
      auto={autoOn}
      setAuto={setAutoOn}
      placeholderByMode={{
        image: "Type a portrait — I'll open Studio and queue it as an image…",
        video: "Type a scene — I'll open Studio and queue it as a video…",
      }}
      onSubmit={({ prompt: p, mode: m }) => {
        try { sessionStorage.setItem("geneo:dockHandoff", JSON.stringify({ prompt: p, mode: m })); } catch {}
        navigate("/studio");
      }}
    />
  );
}
